import React, { Component } from "react"
import { Switch } from "react-router"
import { Query } from "react-apollo"
import SideBar from "../components/SideBar"
import TopBar from "../components/TopBar"
import Loading from "../components/Loading"
import routes from "../routes"
import AppContext from "../shared/AppContext"
import ContactSalesForm, { serviceType } from "../components/ContactSalesForm"
import Modal, { ModalHeader, ModalContent } from "../components/Modal"
import GET_INITIAL_DATA from "../graphql/getInitialData.graphql"
import PAGE_CONTAINER_DATA from "../graphql/pageContainerData.graphql"
import { debouncedTrackPage } from "../lib/analytics"
import "../css/index.scss"

class PageContainer extends Component {
  constructor(props) {
    super(props)

    this.state = {
      user: props.queryProps.data.viewer,
      isInquireModalOpen: false,
      selectedServiceTypes: [],
    }
  }

  // This is a bad pattern, because the reference in a component's render
  // might be stale, we should just access from the window object or come up
  // with a better way to handle the reference in the component.
  setAnalytics = () => {
    this.analytics = window.analytics
  }

  componentDidMount = () => {
    window.analytics.ready(this.setAnalytics)
    const viewer = this.props.queryProps.data.viewer

    if (viewer.isUserAuthenticated) {
      window.analytics.identify(viewer.email, {
        name: viewer.name,
        email: viewer.email,
        roles: viewer.roles,
      })
    }
    this.props.pageName && this.trackPage(this.props.pageName)
  }

  openInquireModal = (service, clickInfo) => {
    this.setState({
      isInquireModalOpen: true,
    })
    this.handleServiceSelection(service)

    if (clickInfo) {
      this.trackEvent(clickInfo)
    }
  }

  closeInquireModal = () => {
    this.setState({
      isInquireModalOpen: false,
      selectedServiceTypes: [],
    })
  }

  handleServiceSelection = (service) => {
    if (service) {
      let selection = [...this.state.selectedServiceTypes]
      let serviceIndex = selection.indexOf(service)

      if (serviceIndex === -1) {
        selection.push(service)
      } else {
        selection.splice(serviceIndex, 1)
      }

      this.setState({
        selectedServiceTypes: selection,
      })
    } else {
      this.setState({
        selectedServiceTypes: serviceType.UpgradePlan,
      })
    }
  }

  trackEvent = (event, properties) => {
    if (this.analytics) {
      this.analytics.track(event, properties)
    } else {
      setTimeout(() => this.trackEvent(event, properties), 1000)
    }
  }

  trackPage = debouncedTrackPage

  render = () => {
    let { fullscreen, topBar, children, initialData, queryProps, ...props } =
      this.props

    let { isInquireModalOpen, selectedServiceTypes } = this.state
    const viewer = this.props.queryProps.data.viewer
    const isUserAuthenticated = !!(viewer && viewer.isUserAuthenticated)
    let showSideBar = isUserAuthenticated

    if (fullscreen && typeof fullscreen === "function") {
      showSideBar = !fullscreen(props)
    } else if (fullscreen && typeof fullscreen === "boolean") {
      showSideBar = !fullscreen
    }

    let showTopBar = showSideBar || topBar
    return (
      <div id="main-container">
        <AppContext.Provider
          value={{
            trackEvent: this.trackEvent,
            trackPage: this.trackPage,
            openInquireModal: this.openInquireModal,
            serviceType: serviceType,
          }}
        >
          {showSideBar && (
            <SideBar
              trackEvent={this.trackEvent}
              viewer={this.props.queryProps.data.viewer}
              openInquireModal={this.openInquireModal}
              serviceType={serviceType}
            />
          )}
          <div
            id="page-container"
            className={`${showSideBar ? "has-side-bar" : "no-side-bar"} ${
              showTopBar ? "has-top-bar" : "no-top-bar"
            }`}
          >
            <Modal
              isOpen={isInquireModalOpen}
              close={this.closeInquireModal}
              allowBackdropClose
            >
              {isInquireModalOpen && (
                <ModalContent className={"medium-large"}>
                  <ModalHeader
                    text="Contact Sales"
                    close={this.closeInquireModal}
                  />
                  <ContactSalesForm
                    closeModal={this.closeInquireModal}
                    selectedServiceTypes={selectedServiceTypes}
                    handleServiceSelection={this.handleServiceSelection}
                    viewer={this.props.viewer}
                  />
                </ModalContent>
              )}
            </Modal>
            {showTopBar && (
              <TopBar
                trackEvent={this.trackEvent}
                openInquireModal={this.openInquireModal}
                serviceType={serviceType}
                {...props}
                {...queryProps}
                minimal={!showSideBar}
              />
            )}
            {children({
              ...props,
              ...initialData,
              trackEvent: this.trackEvent,
              trackPage: this.trackPage,
              openInquireModal: this.openInquireModal,
              serviceType: serviceType,
            })}
          </div>
        </AppContext.Provider>
      </div>
    )
  }
}

const PageContainerWithData = (props) => (
  <Query query={PAGE_CONTAINER_DATA}>
    {({ error, ...queryProps }) => {
      if (error) {
        throw new Error(error)
      }
      if (queryProps.loading && !queryProps.data.viewer) {
        return <Loading />
      }

      return <PageContainer {...props} queryProps={queryProps} />
    }}
  </Query>
)

class App extends Component {
  constructor(props) {
    super(props)
  }

  removeItemsIfUserLoggedOut = () => {
    if (
      !this.props.data.viewer ||
      !this.props.data.viewer.isUserAuthenticated ||
      !this.props.data.viewer.isDeviceAuthenticated
    ) {
      const isViewedToolTip = localStorage.getItem(
        "globalsearchbar.tooltip.viewed"
      )

      localStorage.clear()

      if (isViewedToolTip) {
        localStorage.setItem("globalsearchbar.tooltip.viewed", "true")
      }
    }
  }

  componentDidUpdate = () => {
    this.removeItemsIfUserLoggedOut()
  }

  componentDidMount = () => {
    this.removeItemsIfUserLoggedOut()
  }

  renderRoute = (route, i) => {
    let {
      routeType: RouteComponent,
      component: PageComponent,
      fullscreen,
      topBar,
      isResearch,
      pageName,
      ...rest
    } = route

    let pageProps = {
      initialData: isResearch ? this.props : {},
      fullscreen,
      topBar,
      pageName,
    }

    const render = (routeProps) => (
      <PageContainerWithData
        viewer={this.props.data.viewer}
        {...pageProps}
        {...routeProps}
      >
        {(props) => {
          return <PageComponent {...props} />
        }}
      </PageContainerWithData>
    )

    return <RouteComponent key={i} {...rest} render={render} />
  }

  render() {
    return <Switch>{routes.map(this.renderRoute)}</Switch>
  }
}

const AppWithRoutes = (componentProps) => (
  <Query query={GET_INITIAL_DATA} fetchPolicy="network-only">
    {(queryProps) => {
      if (queryProps.loading) {
        return <Loading />
      }
      if (queryProps.error) throw new Error(queryProps.error)

      let { dashboardRoutes } = queryProps.data

      return <App books={dashboardRoutes} {...queryProps} {...componentProps} />
    }}
  </Query>
)

export default AppWithRoutes
